import { useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { emailContentSchema } from '@publishing-engine/domain/validation';

import { Overlay } from 'src/components/Overlay';
import { EmailContentForm } from '../../EmailModule';
import { Box, Flex } from '@chakra-ui/react';
import { Button, useToast } from '@morressier/alembic';
import { useRejectSubmission } from 'src/api/commands';
import { useEffect } from 'react';
import ReviewSummaryAccordion from './ReviewSummaries';
import NotifyReviewersToggle from './NotifyReviewersToggle';
import { useSubmissionData } from '../../../contexts/SubmissionDataProvider';
import {
  CustomFormFieldDefinition,
  FormFieldFileUploadArray
} from '@publishing-engine/domain/types/article';
import {
  CustomFormFieldComponent,
  FormFieldMultipleFileUpload
} from 'src/components/FormField';
import { JSONSchemaType } from 'ajv';
import { ajvResolver } from '@hookform/resolvers/ajv';
import { EditorialNote, useEditorialDecisionNote } from './EditorialNote';
import useCurrentContextId from '../../../hooks/useCurrentContextId';
import ComposeDecisionEmail from './ComposeDecisionEmail';
import StepHeader from '../../../pages/Organization/public/Journal/SubmissionUpload/components/StepHeader';
import { createEditorialModal } from './EditorialDecisionModalWrapper';
import { buildEmailPayload } from '../../../utils/normalizeEmailRecipients';
import AdjudicatorReviewSummary from './AdjudicatorReviewSummary';
import { useCommandEmailTemplates } from 'src/hooks/useCommandEmailTemplates';
import { useAttachmentSync } from '../../../hooks/useAttachmentSync';
import DecisionLayout from './DecisionLayout';
import ResponseToAuthorField from './ResponseToAuthorField';
import { useCOCContextData } from 'src/contexts/COCDataProvider';
import ModerateCOCComment from './ModerateCOCComment';
import RecommendationRounds from './RecommendationRounds';
import { useQueryClient } from '@tanstack/react-query';

interface Props {
  onClose: () => void;
  submissionId: string;
}
const TOTAL_STEPS = 2;

export const RejectSubmissionModal = createEditorialModal(
  ({ onClose, submissionId }: Props) => {
    const toast = useToast();
    const queryClient = useQueryClient();
    const [currentStep, setCurrentStep] = useState(1);
    const [notifyReviewers, setNotifyReviewers] = useState(false);
    const [notifyReviewerIds, setNotifyReviewerIds] = useState<string[]>([]);
    const { recommedationSummary } = useCOCContextData();
    const submissionContextId = useCurrentContextId() ?? '';

    const {
      workflowResponse: {
        forms: { editorialRejectSubmission },
        articleWorkflow: { id: articleWorkflowId }
      },
      refetchSubmission,
      reviewsSummary,
      organizationId,
      adjudicatorReviewsSummary,
      workflowResponse: { manuscriptId }
    } = useSubmissionData();

    const { hasCustomizableTemplates, templates } = useCommandEmailTemplates({
      organizationId,
      submissionContextId,
      commandName: 'EditorialRejectSubmission'
    });

    const emailContentMethods = useForm<EmailContentForm>({
      defaultValues: {
        emailContent: {
          body: '',
          subject: '',
          recipients: { to: [], cc: [], bcc: [] },
          attachments: []
        }
      },
      resolver: zodResolver(emailContentSchema)
    });

    const emailContent = emailContentMethods.watch('emailContent');
    const { setValue } = emailContentMethods;
    useEffect(() => {
      queryClient.invalidateQueries({
        queryKey: ['reviews-summary', submissionId]
      });
      queryClient.invalidateQueries({
        queryKey: ['recommendation-summary', submissionId]
      });
    }, []);
    useEffect(() => {
      const defaultIndex = templates.findIndex(item => item.default);
      const selectedIndex = defaultIndex === -1 ? 0 : defaultIndex;
      const template = templates?.[selectedIndex || 0];

      if (!template) return;

      setValue(
        'emailContent.recipients.to',
        template.recipients.to
          ?.filter(email => email.trim() !== '')
          .map(email => ({ text: email })) ?? []
      );

      setValue(
        'emailContent.recipients.cc',
        template.recipients.cc
          ?.filter(email => email.trim() !== '')
          .map(email => ({ text: email })) ?? []
      );
      setValue(
        'emailContent.recipients.bcc',
        template.recipients.bcc
          ?.filter(email => email.trim() !== '')
          .map(email => ({ text: email })) ?? []
      );
      setValue('emailContent.subject', template.subject);
      setValue('emailContent.body', template.body);
    }, [templates.length]);
    const goToNextStep = () => {
      if (currentStep === 1) {
        setCurrentStep(prev => prev + 1);
      }
    };
    const goToPreviousStep = () => setCurrentStep(prev => prev - 1);

    const rejectSubmissionMutation = useRejectSubmission(
      submissionId,
      submissionContextId,
      articleWorkflowId,
      refetchSubmission,
      organizationId,
      buildEmailPayload(emailContent),
      notifyReviewerIds
    );

    const { editorNote, setEditorNote, submitEditorNote } =
      useEditorialDecisionNote({
        submissionId,
        submissionContextId,
        organizationId
      });

    const { formFields, schema } = editorialRejectSubmission || {};

    const fields = Object.values(formFields || {}).reduce(
      (acc, val) => ({
        ...acc,
        [val.name]: val as CustomFormFieldDefinition
      }),
      {} as Record<string, CustomFormFieldDefinition>
    );

    const methods = useForm({
      resolver: ajvResolver(schema as JSONSchemaType<unknown>)
    });

    // Auto-sync attachments from first step to email step
    useAttachmentSync({
      sourceForm: methods,
      targetForm: emailContentMethods,
      sourceField: 'attachments',
      targetField: 'emailContent.attachments'
    });

    const { handleSubmit, getValues, watch } = methods;

    const rejectionReason = watch('rejectionReason') as string | undefined;

    const rejectSubmission: SubmitHandler<any> = async (_data, event) => {
      const nativeEvent = event?.nativeEvent as SubmitEvent | undefined;
      const submitter = nativeEvent?.submitter as HTMLButtonElement | null;
      if (!submitter) return;
      const action = submitter.value;
      if (hasCustomizableTemplates && action === 'EditMail') {
        goToNextStep();
      } else {
        await sendDecision();
      }
    };

    const sendDecision = async () => {
      const formValues = getValues();
      const latestRecommendationCycle =
        recommedationSummary?.recommendationRounds?.at(-1)?.cycle?.at(-1);
      const COCcomments = latestRecommendationCycle?.participants
        .map(
          item =>
            (item?.recommendation?.moderatedCommentForAuthor ?? '') ||
            (item?.recommendation?.commentForAuthor ?? '')
        )
        .filter(Boolean)
        .join(' \n');
      const editorComment = COCcomments
        ? `${formValues?.editorComment ?? ''}\n${COCcomments}`
        : formValues?.editorComment ?? '';
      const updatedFormValues = {
        ...formValues,
        editorComment: editorComment
      };
      await rejectSubmissionMutation.mutateAsync(updatedFormValues);

      if (rejectSubmissionMutation.isError) {
        toast({
          variant: 'error',
          title: 'Error rejecting submission',
          description: 'There was an error rejecting the submission.'
        });
      } else {
        await submitEditorNote(editorNote);
        localStorage.setItem(
          `${submissionId}.revisionRequest.editorComment`,
          editorComment
        );
        toast({
          variant: 'success',
          title: 'Submission Rejected',
          description:
            'The author has been notified via email about this decision',
          isClosable: true
        });
        onClose();
      }
    };

    // useEffect(() => {
    //   const savedComment = localStorage.getItem(
    //     `${submissionId}.revisionRequest.editorComment`
    //   );
    //   if (savedComment) {
    //     reset({ editorComment: savedComment });
    //   }
    // }, [reset]);

    const handleStepRender = () => {
      switch (currentStep) {
        case 1:
          return (
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(rejectSubmission)}>
                <Box marginX="auto">
                  <StepHeader
                    currentStep={currentStep}
                    totalSteps={hasCustomizableTemplates ? 2 : 0}
                    heading="Reject Submission"
                    description={
                      reviewsSummary?.length
                        ? 'Proceeding with this decision confirms you have reviewed the submission against the provided Reviews. The author will be informed of this decision along with the following information:'
                        : 'Proceeding with this decision confirms you have reviewed the submission accordingly. The author will be informed of this decision along with the following information:'
                    }
                    marginY={2}
                  />
                </Box>{' '}
                <Box paddingX={1} paddingBottom={8}>
                  <Flex flexDirection="column" gap={6}>
                    {fields.RejectionReason && (
                      <CustomFormFieldComponent
                        config={fields.RejectionReason}
                        placeholder="Select a reason for this rejection"
                      />
                    )}
                    {fields.EditorComment && (
                      <>
                        <ResponseToAuthorField
                          submissionId={submissionId}
                          decisionType="EditorialRejectSubmission"
                          subtypeSelected={rejectionReason}
                        />
                        <CustomFormFieldComponent
                          config={fields.EditorComment}
                          placeholder="Add feedback to the author"
                        />
                      </>
                    )}
                    {fields.Attachments && (
                      <Box marginTop={-3}>
                        <FormFieldMultipleFileUpload
                          submissionId={submissionId}
                          submissionContextId={submissionContextId}
                          config={
                            fields.Attachments as FormFieldFileUploadArray
                          }
                        />
                      </Box>
                    )}
                    {recommedationSummary?.recommendationRounds && (
                      <ModerateCOCComment
                        RecommendationRound={
                          recommedationSummary?.recommendationRounds
                        }
                      />
                    )}
                    {Boolean(
                      reviewsSummary?.length ||
                        adjudicatorReviewsSummary?.length
                    ) && (
                      <Flex direction="column" my={4}>
                        <ReviewSummaryAccordion
                          reviews={reviewsSummary ?? []}
                          submissionId={submissionId}
                        />
                        <AdjudicatorReviewSummary
                          adjudicatorReview={adjudicatorReviewsSummary ?? []}
                        />
                      </Flex>
                    )}

                    <EditorialNote
                      value={editorNote}
                      onChange={setEditorNote}
                    />
                  </Flex>
                </Box>
              </form>
            </FormProvider>
          );
        case 2:
          return (
            <FormProvider {...emailContentMethods}>
              <form onSubmit={emailContentMethods.handleSubmit(sendDecision)}>
                <Box marginX="auto">
                  <StepHeader
                    currentStep={currentStep}
                    totalSteps={TOTAL_STEPS}
                    heading="Compose Email"
                    description={
                      'Compose your email, or select a template to pre-fill the subject and body.'
                    }
                    marginY={2}
                  />
                </Box>

                <ComposeDecisionEmail
                  organizationId={organizationId}
                  submissionContextId={submissionContextId}
                  commandName="EditorialRejectSubmission"
                  subtype={methods.getValues().rejectionReason}
                  formPayload={methods.getValues()}
                />

                <div style={{ display: 'none' }}>
                  <EditorialNote value={editorNote} onChange={setEditorNote} />
                </div>
              </form>
            </FormProvider>
          );
        default:
          return null;
      }
    };

    const renderDecisionSidebar = () => <RecommendationRounds fullWidth />;

    const renderReviewerNotification = () => (
      <NotifyReviewersToggle
        submissionId={submissionId}
        organizationId={organizationId}
        submissionContextId={submissionContextId}
        commandName="EditorialRejectSubmission"
        isEnabled={notifyReviewers}
        selectedReviewerIds={notifyReviewerIds}
        onChange={(enabled, reviewerIds) => {
          setNotifyReviewers(enabled);
          setNotifyReviewerIds(reviewerIds);
        }}
      />
    );

    const renderFooter = () => {
      if (currentStep === 1) {
        return (
          <Flex
            paddingY={4}
            paddingX={6}
            bottom={0}
            zIndex={1}
            width="100%"
            justifyContent="space-between"
            position="sticky"
            bgColor="surfacePrimaryFill"
          >
            <Button variant="caution" onClick={onClose}>
              Exit
            </Button>
            <Flex gap={3}>
              {hasCustomizableTemplates && (
                <Button onClick={goToNextStep} variant="secondary">
                  {'Edit Mail & Submit Decision'}
                </Button>
              )}
              <Button
                onClick={() => methods.handleSubmit(sendDecision)()}
                isLoading={
                  !hasCustomizableTemplates &&
                  rejectSubmissionMutation.isPending
                }
              >
                {'Submit Decision'}
              </Button>
            </Flex>
          </Flex>
        );
      }

      return (
        <Flex
          paddingY={4}
          paddingX={6}
          bottom={0}
          zIndex={1}
          width="100%"
          justifyContent="space-between"
          position="sticky"
          bgColor="surfacePrimaryFill"
        >
          <Button onClick={goToPreviousStep} variant="secondary">
            Back
          </Button>
          <Button
            onClick={() => emailContentMethods.handleSubmit(sendDecision)()}
            isLoading={rejectSubmissionMutation.isPending}
          >
            {'Send decision'}
          </Button>
        </Flex>
      );
    };

    return (
      <Overlay backgroundColor="surfaceSecondaryFill">
        <DecisionLayout
          onClose={onClose}
          manuscriptId={manuscriptId}
          sidebar={currentStep === 1 ? renderDecisionSidebar() : undefined}
        >
          <Box marginTop={2}>
            {handleStepRender()}
            {renderReviewerNotification()}
          </Box>
        </DecisionLayout>
        {renderFooter()}
      </Overlay>
    );
  }
);
